/**
 * Sourcing API client (authed). Mirrors the backend `app/sourcing` schemas.
 *
 * Lists are Attio-style tables: per-list custom fields (rows carry values in
 * `data` keyed by field key), saved views, inline editing. Columns are the AI
 * surface — enrichment fields extract a value per row from a prompt, criterion
 * fields verify a statement per row (yes/no/unclear); both fill via column runs
 * (batched Exa Agent API, polled through the list's column-runs endpoint).
 */

import { apiRequest } from "@lato/client/lib/apiClient";

export type EntityKind = "company" | "person" | "article" | "research_paper";
export type FieldType =
  | "text"
  | "number"
  | "date"
  | "select"
  | "url"
  | "email"
  | "phone"
  | "checkbox"
  | "criterion";
export type FieldSource = "manual" | "enrichment" | "criterion";
export const AI_FIELD_SPEC_MAX_LENGTH = 4_000;
export type AddedVia = "search" | "manual";

export type CriterionVerdict = "yes" | "no" | "unclear";
export const CRITERION_VERDICTS: readonly CriterionVerdict[] = ["yes", "no", "unclear"];

export interface ListField {
  id: string;
  list_id: string;
  /** Stable column identity: keys row `data` and view configs (labels can change; keys can't). */
  key: string;
  label: string;
  type: FieldType;
  options: string[];
  /** `enrichment`/`criterion` = AI-filled by column runs; `manual` = users type. */
  source: FieldSource;
  /** Extraction prompt (enrichment fields). */
  prompt: string | null;
  /** Verification statement (criterion fields). */
  statement: string | null;
  position: number;
  created_at: string;
}

export interface Citation {
  title: string | null;
  snippet: string | null;
  url: string;
}

export interface ListRow {
  id: string;
  list_id: string;
  position: number;
  name: string;
  url: string | null;
  domain: string | null;
  linkedin_url: string | null;
  /** Normalized full-URL dedup key (articles/research papers). */
  url_key: string | null;
  description: string;
  location: string | null;
  company: string | null;
  job_title: string | null;
  author: string | null;
  published_at: string | null;
  /** Custom-field values keyed by field key (criterion fields hold the verdict). */
  data: Record<string, unknown>;
  added_via: AddedVia;
  find_run_id: string | null;
  discovery_citations: Citation[];
  /** Per-AI-field citations, keyed by field key. */
  citations: Record<string, Citation[]>;
  /** Per-AI-field metadata, keyed by field key ({reasoning} for criteria and enrichments). */
  details: Record<string, { reasoning?: string }>;
  created_at: string;
  updated_at: string;
}

export interface RowsPage {
  rows: ListRow[];
}

/** Saved view: visible columns in order (omitted = hidden), sort, AND-combined filters. */
export type FilterOp = "eq" | "neq" | "contains" | "empty" | "not_empty";
export interface FilterClause {
  field: string;
  op: FilterOp;
  value?: string | number | boolean | null;
}
export interface ViewConfig {
  columns: { key: string; width?: number | null }[];
  sort: { key: string; dir: "asc" | "desc" } | null;
  filters: FilterClause[];
}

export interface ListView {
  id: string;
  list_id: string;
  name: string;
  config: ViewConfig;
  position: number;
  created_at: string;
  updated_at: string;
}

export interface SourcingList {
  id: string;
  org_id: string;
  kind: EntityKind;
  name: string;
  description: string;
  created_at: string;
  updated_at: string;
  fields: ListField[];
  views: ListView[];
}

export interface SourcingListSummary {
  id: string;
  kind: EntityKind;
  name: string;
  description: string;
  row_count: number;
  created_at: string;
  updated_at: string;
}

export class SourcingRequestError extends Error {
  constructor(
    public readonly status: number,
    public readonly detail?: unknown
  ) {
    super(typeof detail === "string" && detail ? detail : `Sourcing request failed (${status})`);
  }
}

export async function json<T>(resp: Response): Promise<T> {
  if (!resp.ok) {
    let detail: unknown;
    try {
      detail = ((await resp.json()) as { detail?: unknown })?.detail;
    } catch {
      // no JSON body
    }
    throw new SourcingRequestError(resp.status, detail);
  }
  return (await resp.json()) as T;
}

// ── lists ─────────────────────────────────────────────────────────────────────

export async function listLists(speculative = false): Promise<SourcingListSummary[]> {
  return json(await apiRequest("/sourcing/lists", { speculative }));
}

export async function getList(listId: string, speculative = false): Promise<SourcingList> {
  return json(await apiRequest(`/sourcing/lists/${listId}`, { speculative }));
}

export async function createList(body: {
  name: string;
  kind: EntityKind;
  description?: string;
}): Promise<SourcingList> {
  return json(await apiRequest("/sourcing/lists", { method: "POST", body: JSON.stringify(body) }));
}

export async function updateList(
  listId: string,
  body: { name?: string; description?: string; kind?: EntityKind }
): Promise<SourcingList> {
  return json(
    await apiRequest(`/sourcing/lists/${listId}`, { method: "PATCH", body: JSON.stringify(body) })
  );
}

export async function deleteList(listId: string): Promise<void> {
  const resp = await apiRequest(`/sourcing/lists/${listId}`, { method: "DELETE" });
  if (!resp.ok) throw new SourcingRequestError(resp.status);
}

// ── fields ────────────────────────────────────────────────────────────────────

export interface FieldCreateBody {
  label: string;
  type?: FieldType;
  key?: string;
  options?: string[];
  position?: number;
  source?: FieldSource;
  /** Required for enrichment fields: what to extract per row. */
  prompt?: string;
  /** Required for criterion fields: the statement verified per row. */
  statement?: string;
}

export async function createField(listId: string, body: FieldCreateBody): Promise<ListField> {
  return json(
    await apiRequest(`/sourcing/lists/${listId}/fields`, {
      method: "POST",
      body: JSON.stringify(body),
    })
  );
}

export async function updateField(
  listId: string,
  fieldId: string,
  body: {
    label?: string;
    options?: string[];
    position?: number;
    prompt?: string;
    statement?: string;
  }
): Promise<ListField> {
  return json(
    await apiRequest(`/sourcing/lists/${listId}/fields/${fieldId}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    })
  );
}

export async function deleteField(listId: string, fieldId: string): Promise<void> {
  const resp = await apiRequest(`/sourcing/lists/${listId}/fields/${fieldId}`, {
    method: "DELETE",
  });
  if (!resp.ok) throw new SourcingRequestError(resp.status);
}

// ── rows ──────────────────────────────────────────────────────────────────────

export async function listRows(listId: string): Promise<RowsPage> {
  return json(await apiRequest(`/sourcing/lists/${listId}/rows`));
}

export async function addRowByUrl(
  listId: string,
  body: { url: string; name?: string }
): Promise<ListRow> {
  return json(
    await apiRequest(`/sourcing/lists/${listId}/rows`, {
      method: "POST",
      body: JSON.stringify(body),
    })
  );
}

export async function updateRow(
  listId: string,
  rowId: string,
  body: {
    name?: string;
    url?: string;
    description?: string;
    location?: string;
    data?: Record<string, unknown>;
  }
): Promise<ListRow> {
  return json(
    await apiRequest(`/sourcing/lists/${listId}/rows/${rowId}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    })
  );
}

export interface BulkRowItem {
  url: string;
  name?: string;
  /** Pre-mapped field values keyed by field key. */
  data?: Record<string, unknown>;
}

export interface BulkRowsOutcome {
  added: number;
  duplicates: number;
  failed: { url: string; reason: string }[];
}

export async function addRowsBulk(listId: string, rows: BulkRowItem[]): Promise<BulkRowsOutcome> {
  return json(
    await apiRequest(`/sourcing/lists/${listId}/rows/bulk`, {
      method: "POST",
      body: JSON.stringify({ rows }),
    })
  );
}

export async function deleteRows(listId: string, rowIds: string[]): Promise<{ deleted: number }> {
  return json(
    await apiRequest(`/sourcing/lists/${listId}/rows/bulk-delete`, {
      method: "POST",
      body: JSON.stringify({ row_ids: rowIds }),
    })
  );
}

// ── views ─────────────────────────────────────────────────────────────────────

export async function createView(
  listId: string,
  body: { name: string; config?: ViewConfig; position?: number }
): Promise<ListView> {
  return json(
    await apiRequest(`/sourcing/lists/${listId}/views`, {
      method: "POST",
      body: JSON.stringify(body),
    })
  );
}

export async function updateView(
  listId: string,
  viewId: string,
  body: { name?: string; config?: ViewConfig; position?: number }
): Promise<ListView> {
  return json(
    await apiRequest(`/sourcing/lists/${listId}/views/${viewId}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    })
  );
}

export async function deleteView(listId: string, viewId: string): Promise<void> {
  const resp = await apiRequest(`/sourcing/lists/${listId}/views/${viewId}`, {
    method: "DELETE",
  });
  if (!resp.ok) throw new SourcingRequestError(resp.status);
}

// ── column runs (AI cell fills) ───────────────────────────────────────────────

export type ColumnRunStatus = "pending" | "running" | "completed" | "failed" | "canceled";
/** `empty` fills only cells without a value; `all` clears the column and re-runs. */
export type ColumnRunMode = "empty" | "all";

export interface ColumnRun {
  id: string;
  list_id: string;
  field_id: string;
  field_key: string;
  mode: ColumnRunMode;
  status: ColumnRunStatus;
  total_rows: number;
  processed_rows: number;
  failed_rows: number;
  cost_dollars_total: number | null;
  error: string | null;
  created_at: string;
  completed_at: string | null;
}

export function isColumnRunSettled(status: ColumnRunStatus | undefined): boolean {
  return status === "completed" || status === "failed" || status === "canceled";
}

export async function createColumnRun(
  listId: string,
  fieldId: string,
  body: { mode?: ColumnRunMode } = {}
): Promise<ColumnRun> {
  return json(
    await apiRequest(`/sourcing/lists/${listId}/fields/${fieldId}/run`, {
      method: "POST",
      body: JSON.stringify(body),
    })
  );
}

/** The list's runs, live ones first — the is-anything-running poll signal. */
export async function listColumnRuns(listId: string): Promise<ColumnRun[]> {
  return json(await apiRequest(`/sourcing/lists/${listId}/column-runs`));
}

export async function getColumnRun(runId: string): Promise<ColumnRun> {
  return json(await apiRequest(`/sourcing/column-runs/${runId}`));
}

export async function cancelColumnRun(runId: string): Promise<ColumnRun> {
  return json(await apiRequest(`/sourcing/column-runs/${runId}/cancel`, { method: "POST" }));
}

// ── find runs (Websets-style search that seeds rows) ──────────────────────────

export interface FindRun {
  id: string;
  list_id: string;
  query: string;
  criteria: Array<{ key: string; label: string; statement: string }>;
  target_count: number;
  status: ColumnRunStatus;
  found_rows: number;
  candidate_rows: number;
  rejected_rows: number;
  unclear_rows: number;
  cost_dollars_total: number | null;
  error: string | null;
  created_at: string;
  completed_at: string | null;
}

export interface PlanCriterion {
  label: string;
  statement: string;
}

export interface PlanEnrichment {
  label: string;
  prompt: string;
}

/** The plan agent's breakdown of a plain-English ask (the preview step). */
export interface SearchPlan {
  name: string;
  kind: EntityKind;
  criteria: PlanCriterion[];
  enrichments: PlanEnrichment[];
}

/** Omitting `kind` lets the plan agent infer companies vs people from the query. */
export async function planSearch(body: { query: string; kind?: EntityKind }): Promise<SearchPlan> {
  return json(await apiRequest("/sourcing/plan", { method: "POST", body: JSON.stringify(body) }));
}

/** Create the list, criterion columns, and initial verified search atomically. */
export async function createListFromPlan(body: {
  query: string;
  name: string;
  kind: EntityKind;
  criteria: PlanCriterion[];
  enrichments: PlanEnrichment[];
  count: number;
}): Promise<{ list: SourcingList; run: FindRun }> {
  return json(
    await apiRequest("/sourcing/lists/from-plan", {
      method: "POST",
      body: JSON.stringify(body),
    })
  );
}

export async function createFindRun(
  listId: string,
  /** Omitting `query` lets the backend derive it from the list (criteria → last search → name). */
  body: { query?: string; count: number }
): Promise<FindRun> {
  return json(
    await apiRequest(`/sourcing/lists/${listId}/find`, {
      method: "POST",
      body: JSON.stringify(body),
    })
  );
}

/** The list's find runs, live ones first — the search-progress poll signal. */
export async function listFindRuns(listId: string): Promise<FindRun[]> {
  return json(await apiRequest(`/sourcing/lists/${listId}/find-runs`));
}

export async function cancelFindRun(runId: string): Promise<FindRun> {
  return json(await apiRequest(`/sourcing/find-runs/${runId}/cancel`, { method: "POST" }));
}

// ── instant search ────────────────────────────────────────────────────────────

export type InstantSearchType = "fast" | "auto" | "deep";

/** Exa's plain-/search category set (which buttons the UI shows is a frontend choice). */
export type ExaCategory =
  | "company"
  | "research paper"
  | "news"
  | "pdf"
  | "personal site"
  | "financial report"
  | "people";

export interface InstantResult {
  url: string;
  title: string | null;
  author: string | null;
  published_date: string | null;
  favicon: string | null;
  image: string | null;
  score: number | null;
  text: string | null;
  highlights: string[];
}

export interface GroundingCitation {
  url: string;
  title: string | null;
}

/** Citations backing one field of a synthesized output. */
export interface GroundingEntry {
  field: string;
  citations: GroundingCitation[];
}

/** Synthesized answer (text or structured) when output_schema was requested. */
export interface SynthOutput {
  content: unknown;
  grounding: GroundingEntry[];
}

export interface InstantSearchResponse {
  results: InstantResult[];
  resolved_search_type: string | null;
  output: SynthOutput | null;
}

export async function instantSearch(body: {
  query: string;
  category?: ExaCategory;
  type?: InstantSearchType;
  num_results?: number;
  output_schema?: Record<string, unknown>;
}): Promise<InstantSearchResponse> {
  return json(
    await apiRequest("/sourcing/instant-search", { method: "POST", body: JSON.stringify(body) })
  );
}
